import { useEffect, useState, useRef } from "react";
import api, { inr } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Search } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function NewOrderDialog({ open, onClose, customers, products, onSaved, editOrder }) {
  const [customerId, setCustomerId] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const chipsRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCustomerId(editOrder ? editOrder.customer_id : "");
      setOrderDate(editOrder ? editOrder.order_date : new Date().toISOString().slice(0, 10));
      setItems(editOrder ? editOrder.items.map((i) => ({ _k: uid(), product_id: i.product_id, product_name: i.product_name, qty: i.qty, rate: i.rate, note: i.note || "" })) : []);
      setNote(editOrder ? editOrder.note || "" : "");
      setProdSearch("");
      if (chipsRef.current) chipsRef.current.scrollTop = 0;
    }
  }, [open, editOrder]);

  useEffect(() => {
    if (chipsRef.current) chipsRef.current.scrollTop = 0;
  }, [prodSearch]);

  const addProduct = (p) => {
    setItems([...items, { _k: uid(), product_id: p.id, product_name: p.name, qty: 1, rate: p.default_rate, note: "" }]);
  };

  const setItem = (idx, field, value) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [field]: value };
    setItems(copy);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const total = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.rate || 0), 0);

  const save = async (keepOpen) => {
    if (!customerId) return toast.error("Please select a customer first");
    if (items.length === 0) return toast.error("Select at least one product");
    setBusy(true);
    const payload = {
      customer_id: customerId,
      order_date: orderDate,
      note,
      items: items.map((i) => ({
        product_id: i.product_id,
        product_name: i.product_name,
        qty: Number(i.qty),
        rate: Number(i.rate),
        note: i.note || "",
      })),
    };
    try {
      if (editOrder) {
        await api.put(`/orders/${editOrder.id}`, payload);
        toast.success(`Order updated - ${inr(total)}`);
        onSaved(false);
      } else {
        await api.post("/orders", payload);
        toast.success(`Order saved - ${inr(total)}`);
        if (keepOpen) {
          setItems([]);
          setNote("");
        }
        onSaved(keepOpen);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order could not be saved");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editOrder ? "Edit Order" : "New Order"}</DialogTitle>
          <DialogDescription>Select a customer, click products and fill qty/rate.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Customer</label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="order-customer-select">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{c.firm ? ` (${c.firm})` : ""} - {c.mobile}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customers.length === 0 && (
              <p className="text-xs text-pink-600">Add a customer first from the Customers tab.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Order Date</label>
            <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} data-testid="order-date-input" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Click products to add</label>
            <div className="relative w-44">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search product..."
                value={prodSearch}
                onChange={(e) => setProdSearch(e.target.value)}
                className="pl-7 h-7 text-xs"
                data-testid="order-product-search"
              />
            </div>
          </div>
          <div ref={chipsRef} className="flex flex-wrap gap-2 max-h-48 overflow-y-auto py-0.5" data-testid="order-product-select">
            {products.filter((p) => p.name.toLowerCase().includes(prodSearch.toLowerCase())).map((p) => {
              const count = items.filter((i) => i.product_id === p.id).length;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`product-chip-${p.id.slice(0, 8)}`}
                  onClick={(e) => { addProduct(p); e.currentTarget.blur(); }}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    count > 0
                      ? "bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700"
                      : "bg-cyan-50 text-cyan-800 border-cyan-200 hover:bg-cyan-100"
                  }`}
                >
                  <Plus className="h-3 w-3" />
                  {p.name}
                  <span className="font-normal opacity-70">({inr(p.default_rate)}/{p.unit})</span>
                  {count > 0 && (
                    <span className="ml-0.5 bg-white text-cyan-700 rounded-full px-1.5 py-px text-[10px] font-bold" data-testid={`product-count-${p.id.slice(0, 8)}`}>×{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {items.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Product</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-24">Qty</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-28">Rate</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Amount</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it._k || idx} className="border-t border-border">
                    <td className="px-3 py-2">
                      <p className="font-medium">{it.product_name}</p>
                      <Input
                        placeholder="Item note (short)"
                        value={it.note || ""}
                        onChange={(e) => setItem(idx, "note", e.target.value)}
                        data-testid={`order-item-note-${idx}`}
                        className="h-6 text-xs mt-1 w-44 border-slate-200"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" min="0" step="any"
                        value={it.qty}
                        onChange={(e) => setItem(idx, "qty", e.target.value)}
                        data-testid={`order-qty-input-${idx}`}
                        className="h-8 w-20"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" min="0" step="any"
                        value={it.rate}
                        onChange={(e) => setItem(idx, "rate", e.target.value)}
                        data-testid={`order-rate-input-${idx}`}
                        className="h-8 w-24"
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold" data-testid={`order-amount-display-${idx}`}>
                      {inr(Number(it.qty || 0) * Number(it.rate || 0))}
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeItem(idx)} data-testid={`order-item-remove-${idx}`} className="text-red-500 hover:text-red-600">
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2.5 bg-slate-50 border-t border-border flex justify-between items-center">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Total Amount</span>
              <span className="font-heading text-lg font-bold text-cyan-700" data-testid="order-total-display">{inr(total)}</span>
            </div>
          </div>
        )}

        <Textarea
          placeholder="Note (optional) - e.g. add eyelets, lamination..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="order-note-input"
          rows={2}
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="order-cancel-btn">Cancel</Button>
          {!editOrder && (
            <Button variant="outline" onClick={() => save(true)} disabled={busy} data-testid="order-save-more-btn" className="border-cyan-500 text-cyan-700 hover:bg-cyan-50">
              {busy ? "Saving..." : "Save & Add More"}
            </Button>
          )}
          <Button onClick={() => save(false)} disabled={busy} data-testid="order-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
            {busy ? "Saving..." : editOrder ? "Update Order" : "Save Order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
